/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (document.readyState !== 'loading' && this.hasChildNodes()) this.init();
    }

    connectedCallback() {
      if (this.form) return;
      const connect = () => {
        if (!this.isConnected || this.form) return;
        this.init();
        if (!this.form && !this.formObserver) {
          this.formObserver = new MutationObserver(() => this.init());
          this.formObserver.observe(this, { childList: true, subtree: true });
        }
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', connect, { once: true });
      } else {
        connect();
      }
    }

    init() {
      if (this.form) return;
      const form = this.querySelector('.js-product-form');
      const variantInput = form && form.querySelector('[name="id"]');
      const submitButton = this.querySelector('[name="add"]');
      if (!form || !variantInput || !submitButton) return;
      this.form = form;
      if (this.formObserver) { this.formObserver.disconnect(); this.formObserver = null; }
      if (this.form) {
        variantInput.disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        if (this.dataset.caPdpParity === 'true') { this.initDtrProgramming(); this.initProgramming(); this.initEvxColor(); this.initBcpoDrawer(); }

        if (this.dataset.showQuantitySelector === 'true') {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            this.lineItemChangeListener = this.lineItemChangeListener
              || this.handleLineItemChange.bind(this);
            document.addEventListener('on:line-item:change', (evt) => {
              this.lineItemChangeListener(evt);
            });
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        document.addEventListener('on:variant:change', this.setMaximumInputQuantity.bind(this));
      }
    }

    initDtrProgramming() {
      const config = window.caDtrProgramming;
      const id = this.form.querySelector('[name="product-id"]');
      const select = this.form.querySelector('[name="properties[Programming]"]');
      if (!config || !id || config.productId !== id.value || !select) return;
      const current = Array.from(select.options).filter(option => !option.disabled && option.value).map(option => option.value);
      if (JSON.stringify(current) !== JSON.stringify(config.nativeChoices)) return;
      const selected = config.choices.includes(select.value) ? select.value : '';
      const placeholder = select.options[0].cloneNode(true);
      placeholder.value = ''; placeholder.disabled = true; placeholder.selected = !selected;
      const choices = config.choices.map(value => {
        const option = document.createElement('option');
        option.value = value; option.textContent = value; option.selected = value === selected;
        return option;
      });
      select.replaceChildren(placeholder, ...choices);
      select.dataset.caDtrProgramming = 'true';
    }

    initProgramming() {
      this.programmingSelect = this.form.querySelector('[name="properties[Programming]"]');
      if (!this.programmingSelect) return;
      // Validate before an app's button handler can bypass the native submit event.
      // Listen on the stable ancestor because BCPO may replace the Add button.
      this.programmingCaptureGuard = (event) => {
        if (event.type === 'submit') {
          if (event.target !== this.form) return;
        } else {
          const button = event.target.closest && event.target.closest('[name="add"]');
          if (!button || button.form !== this.form) return;
        }
        if (this.validateProgramming()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      this.addEventListener('click', this.programmingCaptureGuard, true);
      this.addEventListener('submit', this.programmingCaptureGuard, true);
      this.programmingGate = this.querySelector('.js-dynamic-checkout-gate');
      const sync = () => {
        const selected = Boolean(this.programmingSelect.value);
        if (this.programmingGate) {
          this.programmingGate.dataset.gateBlocked = selected ? 'false' : 'true';
          const wallets = this.programmingGate.querySelector('.js-programming-wallets');
          if (wallets) wallets.inert = !selected;
        }
        const upload = this.form.querySelector('#custom_file');
        if (upload) {
          const show = this.programmingSelect.value.includes('Custom');
          upload.style.display = show ? 'block' : 'none';
          const file = upload.querySelector('input[type="file"]');
          if (file && !show) file.value = '';
        }
        if (selected) {
          this.programmingSelect.removeAttribute('aria-invalid');
          const fieldset = this.programmingSelect.closest('fieldset');
          if (fieldset) fieldset.classList.remove('ca-prog-error');
          const error = this.querySelector('.js-form-error');
          if (error && error.textContent === 'Please choose a Programming option before adding to cart.') {
            error.hidden = true; error.textContent = '';
          }
        }
      };
      this.programmingSelect.addEventListener('change', sync);
      sync();
      this.initDuplicateProgramming();
    }

    validateProgramming() {
      const programming = this.programmingSelect;
      if (!programming || programming.value) return true;
      this.setErrorMsgState('Please choose a Programming option before adding to cart.');
      programming.setAttribute('aria-invalid', 'true');
      const fieldset = programming.closest('fieldset');
      if (fieldset) fieldset.classList.add('ca-prog-error');
      programming.scrollIntoView({ behavior: 'smooth', block: 'center' });
      programming.focus({ preventScroll: true });
      return false;
    }

    initDuplicateProgramming() {
      const section = this.closest('.cc-main-product');
      const nativeSelect = this.programmingSelect;
      const productId = this.form.querySelector('[name="product-id"]');
      if (!section || !nativeSelect || !productId) return;
      const originals = new WeakMap();
      const values = (select) => Array.from(select.options)
        .filter((option) => !option.disabled && option.value !== '')
        .map((option) => option.value).sort();
      const syncDuplicates = () => {
        const data = window.bcpo_data;
        if (!data || String(data.product_id) !== productId.value) return;
        const nativeValues = values(nativeSelect);
        section.querySelectorAll('.js-instalments-form .bcpo select[name="properties[Programming]"]').forEach((duplicate) => {
          const wrapper = duplicate.closest('.selector-wrapper');
          if (!wrapper || duplicate.form === this.form) return;
          const definition = (data.virtual_options || []).find((option) => option.unique === wrapper.dataset.unique);
          const unpriced = definition && definition.title === 'Programming' && definition.type === 'default'
            && Object.keys(definition).every((key) => ['title', 'type', 'unique', 'preselect_value', 'values'].includes(key))
            && Array.isArray(definition.values) && definition.values.length > 0
            && definition.values.every((option) => Object.keys(option).length === 1 && typeof option.key === 'string');
          const sameChoices = nativeValues.length > 0
            && JSON.stringify(nativeValues) === JSON.stringify(values(duplicate))
            && unpriced && JSON.stringify(nativeValues) === JSON.stringify(definition.values.map((option) => option.key).sort());
          if (!sameChoices) {
            const previous = originals.get(wrapper);
            if (previous) {
              wrapper.hidden = previous.hidden;
              if (previous.aria === null) wrapper.removeAttribute('aria-hidden'); else wrapper.setAttribute('aria-hidden', previous.aria);
              if (previous.display) wrapper.style.setProperty('display', previous.display, previous.priority); else wrapper.style.removeProperty('display');
              originals.delete(wrapper);
            }
            return;
          }
          if (!originals.has(wrapper)) originals.set(wrapper, {
            hidden: wrapper.hidden, aria: wrapper.getAttribute('aria-hidden'),
            display: wrapper.style.getPropertyValue('display'), priority: wrapper.style.getPropertyPriority('display')
          });
          wrapper.hidden = true;
          wrapper.setAttribute('aria-hidden', 'true');
          wrapper.style.setProperty('display', 'none', 'important');
          if (duplicate.value !== nativeSelect.value) {
            duplicate.value = nativeSelect.value;
            duplicate.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      };
      nativeSelect.addEventListener('change', syncDuplicates);
      this.duplicateProgrammingObserver = new MutationObserver(syncDuplicates);
      this.duplicateProgrammingObserver.observe(section, { childList: true, subtree: true });
      syncDuplicates();
    }

    initEvxColor() {
      const productId = this.form.querySelector('[name="product-id"]');
      const section = this.closest('.cc-main-product');
      if (!section || !productId || productId.value !== '7458615525623') return;
      const originals = new Map();
      const values = (select) => Array.from(select.options).filter(option => option.value && !option.disabled).map(option => option.value).sort();
      const expected = JSON.stringify(['Black', 'Yellow']);
      const restore = () => {
        originals.forEach((previous, wrapper) => {
          wrapper.hidden = previous.hidden;
          if (previous.aria === null) wrapper.removeAttribute('aria-hidden'); else wrapper.setAttribute('aria-hidden', previous.aria);
          if (previous.display) wrapper.style.setProperty('display', previous.display, previous.priority); else wrapper.style.removeProperty('display');
          delete wrapper.dataset.caEvxColor;
        });
        originals.clear();
      };
      const safeDefinition = (definition, virtual) => definition && definition.title === 'Color' && definition.type === 'default'
        && Object.keys(definition).every(key => (virtual ? ['title', 'type', 'unique', 'preselect_value', 'required', 'values'] : ['title', 'type', 'unique', 'values']).includes(key))
        && (!virtual || (definition.preselect_value === '' && definition.required === 'on'))
        && Array.isArray(definition.values) && definition.values.every(option => Object.keys(option).length === 1 && typeof option.key === 'string')
        && JSON.stringify(definition.values.map(option => option.key).sort()) === expected;
      const sync = () => {
        const data = window.bcpo_data;
        const native = section.querySelectorAll('.bcpo select.single-option-selector[data-option="option2"][aria-label="Color"]');
        const duplicate = section.querySelectorAll('.bcpo select[name="properties[Color]"]');
        const nativeDefinition = data && data.shopify_options && data.shopify_options[1];
        const virtual = data && Array.isArray(data.virtual_options) && data.virtual_options.filter(option => option.title === 'Color');
        const wrapper = duplicate.length === 1 && duplicate[0].closest('.selector-wrapper');
        const exact = data && String(data.product_id) === productId.value && native.length === 1 && duplicate.length === 1 && wrapper
          && safeDefinition(nativeDefinition, false) && nativeDefinition.unique === 1
          && virtual && virtual.length === 1 && virtual[0].unique === '_20irbol4l' && safeDefinition(virtual[0], true)
          && wrapper.dataset.unique === virtual[0].unique
          && JSON.stringify(values(native[0])) === expected && JSON.stringify(values(duplicate[0])) === expected;
        if (!exact) { restore(); return; }
        if (!originals.has(wrapper)) originals.set(wrapper, {
          hidden: wrapper.hidden, aria: wrapper.getAttribute('aria-hidden'),
          display: wrapper.style.getPropertyValue('display'), priority: wrapper.style.getPropertyPriority('display')
        });
        wrapper.hidden = true;
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.style.setProperty('display', 'none', 'important');
        wrapper.dataset.caEvxColor = 'synced';
        if (duplicate[0].value !== native[0].value) {
          duplicate[0].value = native[0].value;
          duplicate[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      this.evxColorSection = section;
      this.evxColorSync = sync;
      section.addEventListener('change', sync);
      this.evxColorObserver = new MutationObserver(sync);
      this.evxColorObserver.observe(section, { childList: true, subtree: true });
      sync();
    }

    initBcpoDrawer() {
      const productId = this.form.querySelector('[name="product-id"]');
      if (!productId) return;
      const bind = () => {
        const app = window.bcpo;
        const data = window.bcpo_data;
        if (!this.isConnected || this.bcpoAfterAdd || !app || !app.$ || !data || String(data.product_id) !== productId.value) return;
        this.bcpoAfterAdd = () => {
          const drawer = document.querySelector('cart-drawer');
          if (!this.isConnected || !drawer || typeof drawer.refreshCartDrawer !== 'function') return undefined;
          // BCPO's supported afterAdd hook skips its default /cart navigation when truthy.
          // Its add, required options, uploads and price adjustments have already run.
          this.finishBcpoDrawerAdd(drawer);
          return true;
        };
        app.$(document).off('bcpo:afterAdd.caCanopyDrawer').on('bcpo:afterAdd.caCanopyDrawer', this.bcpoAfterAdd);
        if (this.bcpoBindObserver) { this.bcpoBindObserver.disconnect(); this.bcpoBindObserver = null; }
      };
      bind();
      if (!this.bcpoAfterAdd) {
        this.bcpoBindObserver = new MutationObserver(bind);
        this.bcpoBindObserver.observe(this.closest('.cc-main-product') || this, { childList: true, subtree: true });
        window.addEventListener('load', bind, { once: true });
      }
    }

    async finishBcpoDrawerAdd(drawer) {
      try {
        const cartRequest = fetch(`${theme.routes.cart}.js`, { cache: 'no-store' })
          .then((response) => response.ok ? response.json() : null).catch(() => null);
        const refreshed = await drawer.refreshCartDrawer(true, true);
        const cart = await cartRequest;
        if (cart && window.bcpo) {
          window.bcpo.cart2 = cart;
          if (typeof window.bcpo.fixCartPrices === 'function') window.bcpo.fixCartPrices(false);
        }
        if (!refreshed) this.setErrorMsgState('Your item was added, but the cart drawer could not load. Please try opening the cart again.');
      } catch (error) {
        this.setErrorMsgState('Your item was added, but the cart drawer could not load. Please try opening the cart again.');
      } finally {
        const section = this.closest('.cc-main-product');
        if (section) section.querySelectorAll('.r-apps-loader').forEach((loader) => loader.classList.add('r-apps-loader--hidden'));
        const button = this.form.querySelector('[name="add"]');
        if (button) { button.disabled = false; button.removeAttribute('aria-disabled'); button.classList.remove('is-loading'); }
      }
    }

    disconnectedCallback() {
      if (this.bcpoBindObserver) this.bcpoBindObserver.disconnect();
      if (this.bcpoAfterAdd && window.bcpo && window.bcpo.$) window.bcpo.$(document).off('bcpo:afterAdd.caCanopyDrawer', this.bcpoAfterAdd);
      if (this.duplicateProgrammingObserver) this.duplicateProgrammingObserver.disconnect();
      if (this.evxColorObserver) this.evxColorObserver.disconnect();
      if (this.evxColorSection && this.evxColorSync) this.evxColorSection.removeEventListener('change', this.evxColorSync);
      if (this.formObserver) { this.formObserver.disconnect(); this.formObserver = null; }
      document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const variantId = Number(this.form.querySelector('[name="id"]').value);
            const inventoryJson = JSON.parse(dataEl.textContent);
            const inventory = inventoryJson.find((item) => item.id === variantId);

            if (inventory.inventory_management.length !== 0 && inventory.inventory_quantity > 0 && inventory.inventory_policy !== 'continue') {
              quantityInput.max = inventory.inventory_quantity;

              const currentQuantity = Number(quantityInput.value);
              if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                quantityInput.value = quantityInput.max;
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    handleLineItemChange(evt) {
      if (evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.value);
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      if (this.dataset.caPdpParity === 'true' && !this.validateProgramming()) return;
      this.cartDrawer = document.querySelector('cart-drawer');

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Get all quick order lists on the page
      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          // sections.push({
          //   id: `quick-order-list_${sectionId}`,
          //   section: sectionId,
          //   selector: `#quick-order-list_${sectionId}`
          // });
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
                  || theme.settings.afterAtc === 'drawer')
              );

              // Update the quick order lists on the page
              if (quickOrderSections.length) {
                quickOrderSections.forEach((quickOrderSection) => {
                  const sectionEl = document.getElementById(quickOrderSection.id);
                  if (!sectionEl) return;

                  const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                  el.innerHTML = ProductForm.getElementHTML(
                    data.sections[quickOrderSection.section],
                    quickOrderSection.selector
                  );
                });
              }
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refreshCartItems();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        let newQuantity = 1;

        // Check if product was already in the cart
        if (itemInOldCart) {
          newQuantity = (itemInOldCart.quantity === data.quantity)
            ? itemInOldCart.quantity : data.quantity;
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));

          // Update the quantity selectors if needed
          if (this.quantitySelector) {
            this.setAttribute('data-show-quantity-selector', 'true');
            this.quantityInput.value = newQuantity;
            this.quantitySelector.currentQty = this.quantityInput.dataset.initialValue;
          }
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    updateQuantityState(newQuantity) {
      this.quantityInput.value = newQuantity;
      this.quantitySelector.currentQty = newQuantity;
      this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
    }

    async updateQuantity(quantity) {
      this.quantitySelector.setAttribute('aria-disabled', 'true');
      const lineErrors = this.querySelector('.js-product-form-errors');

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      fetchRequestOpts.body = JSON.stringify({
        id: this.dataset.variantId,
        quantity
      });

      try {
        const response = await fetch(theme.routes.cartChange, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        const oldQuantity = parseInt(this.quantityInput.dataset.initialValue, 10);
        this.updateQuantityState(oldQuantity);
      } finally {
        this.updateQuantityState(parseInt(quantity, 10));
        this.quantitySelector.removeAttribute('aria-disabled');
        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
